import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { BugLoggerProvider, ErrorBoundary } from '@vulog-internal/bug-logger-react';

import { getBugLogger, subscribeBugLogger } from '../../helpers/bugLogger';

/** Neutral, provider-free fallback shown when a render error is caught. */
function ErrorFallback() {
    return (
        <div
            style={{
                fontFamily: 'system-ui, sans-serif',
                maxWidth: 480,
                margin: '80px auto',
                padding: '0 16px',
                textAlign: 'center',
            }}
        >
            <h2>Something went wrong</h2>
            <p>An unexpected error occurred. Please reload the page.</p>
            <button type="button" onClick={() => window.location.reload()}>
                Reload
            </button>
        </div>
    );
}

/**
 * Root wrapper that wires the Vulog Bug Logger into the app. It exposes the
 * logger (created from the runtime tenant config by `initBugLogger`) through
 * `<BugLoggerProvider>`, while an `<ErrorBoundary>` captures React render errors
 * and shows a fallback.
 *
 * The boundary is always mounted, so it catches render errors and shows the
 * fallback even before the logger exists; error reporting to the logger begins
 * once `initBugLogger` resolves.
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element} the app subtree wrapped with logging.
 */
export default function BugLoggerRoot({ children }) {
    const [logger, setLogger] = useState(getBugLogger());

    useEffect(() => subscribeBugLogger(setLogger), []);

    return (
        <BugLoggerProvider logger={logger}>
            <ErrorBoundary fallback={<ErrorFallback />}>{children}</ErrorBoundary>
        </BugLoggerProvider>
    );
}

BugLoggerRoot.propTypes = {
    children: PropTypes.node.isRequired,
};
